import {
  BanIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  DownloadIcon,
  DuplicateIcon,
  FilmIcon,
  GlobeIcon,
  MicrophoneIcon,
  MusicNoteIcon,
  PhotographIcon,
} from '@heroicons/react/outline';
import type { ComponentType, SVGProps } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, SectionHeading, tone } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { useMusicT } from './kit';

type Item = {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  body: string;
};

/** What every song comes with. */
export default function Capabilities() {
  const { t } = useMusicT();
  const reveal = useReveal<HTMLUListElement>(0.1);

  const items: Item[] = [
    {
      Icon: MicrophoneIcon,
      title: t('Real vocals'),
      body: t(
        'A male or female voice, or let the AI choose. Or tick Instrumental for music without singing.',
      ),
    },
    {
      Icon: DocumentTextIcon,
      title: t('Your own lyrics'),
      body: t(
        'Paste up to 3,000 characters on paid plans and mark the verses, choruses and bridge.',
      ),
    },
    {
      Icon: MusicNoteIcon,
      title: t('Any genre or mood'),
      body: t(
        'Pop, rap, rock, country, lo-fi, orchestral and anything in between, or a blend.',
      ),
    },
    {
      Icon: DuplicateIcon,
      title: t('Two versions every time'),
      body: t('Every generation gives you two takes of the song to pick from.'),
    },
    {
      Icon: PhotographIcon,
      title: t('Cover art'),
      body: t('Each version comes with its own cover image.'),
    },
    {
      Icon: DownloadIcon,
      title: t('MP3 and WAV'),
      body: t(
        'MP3 for sharing and listening, uncompressed WAV for editing and mastering.',
      ),
    },
    {
      Icon: BanIcon,
      title: t('Leave sounds out'),
      body: t(
        'List styles to avoid, like autotune or heavy distortion, in Exclude styles.',
      ),
    },
    {
      Icon: FilmIcon,
      title: t('Lyric videos'),
      body: t('Turn a song into a simple video with the lyrics on screen.'),
    },
    {
      Icon: GlobeIcon,
      title: t('Many languages'),
      body: t(
        'Write your description or lyrics in the language you want the song sung in.',
      ),
    },
    {
      Icon: BriefcaseIcon,
      title: t('Commercial use'),
      body: t(
        'Songs made on a paid plan come with full commercial rights, even after you cancel.',
      ),
    },
  ];

  return (
    <section className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          size='md'
          title={t('Everything a song needs.')}
          tail={t('In one place.')}
        />
        <ul
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-5',
          )}
        >
          {items.map(({ Icon, title, body }, index) => (
            <li
              key={title}
              className={styles.rv}
              style={delay((index % 5) * 60)}
            >
              <span className='flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-400/20'>
                <Icon className='h-5 w-5' aria-hidden='true' />
              </span>
              <h3 className={clsxm('mt-4 text-[17px] font-semibold', tone.ink)}>
                {title}
              </h3>
              <p className={clsxm('mt-2 text-[15px] leading-6', tone.body)}>
                {body}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
